import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { AddOnItemName, CustomDomainInfo } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import StripeCheckout from "components/StripeCheckout";
import { fetcherAuth } from "utils/api";
import { getSource, trackEvent } from "utils/tracking";

interface IProps {
  open: boolean;
  handeClose: () => void;
}

const CustomDomainDialog = ({ open, handeClose }: IProps) => {
  const { plans } = useContext(SubscriptionContext);
  const { currentUser, currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const source = getSource();
  const { setErrorAlert } = useContext(AlertContext);
  const [step, setStep] = useState<number>(0);
  const [domain, setDomain] = useState<string>("");
  const [searchResults, setSearchResults] = useState<CustomDomainInfo[] | null>(
    null,
  );
  const [selectedDomain, setSelectedDomain] = useState<CustomDomainInfo | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const customDomainExample = () => {
    if (!currentUser?.name) {
      return `ugcbybento.com`;
    }
    return `ugcby${currentUser?.name.split(" ")[0].toLowerCase()}.com`;
  };

  const onChange = (e: CustomEvent) => {
    const { value } = e.target;
    setDomain(value);
  };

  const fetchDomainPrice = async (d: CustomDomainInfo) => {
    try {
      const { domain: resDomain } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/custom-domains/price?domain=${d.domain}`,
        "GET",
        {},
      );

      setSearchResults((prev) => {
        if (!prev) return null;
        return prev.map((domainObj) => {
          if (domainObj.domain === resDomain.domain) {
            return resDomain;
          }
          return domainObj;
        });
      });
    } catch (error) {}
  };

  const onSubmit = async (e: CustomEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/custom-domains?domain=${domain}`,
        "GET",
        {},
      );
      setSearchResults(res.domains);
      for (const domain of res.domains) {
        fetchDomainPrice(domain);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 0 || step === 3) {
      trackEvent("Custom Domain Dialog Closed Dialog", {
        step,
      });
      handeClose();
    } else if (step === 1) {
      trackEvent("Custom Domain Dialog Go Back", {
        step,
      });
      setStep(0);
    } else if (step === 2) {
      trackEvent("Custom Domain Dialog Go Back", {
        step,
      });
      setStep(1);
    }
  };

  return (
    <Dialog
      open={open}
      scroll="body"
      onClose={handeClose}
      fullScreen={isMobileScreen}
      fullWidth
      maxWidth={step === 3 ? "sm" : "md"}
    >
      {step === 0 && (
        <form onSubmit={onSubmit}>
          <DialogTitle>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography gutterBottom variant="h4">
                Get a new domain
              </Typography>
              <Typography
                color="textSecondary"
                sx={{ maxWidth: "85%", textAlign: "center" }}
              >
                Includes set-up with Gmail Inbox, configured with top-tier
                deliverability (SPF, DKIM, DMARC included) and monitoring of
                spam reports
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs>
                <TextField
                  size="small"
                  onChange={onChange}
                  value={domain}
                  fullWidth
                  required
                  placeholder={customDomainExample()}
                />
              </Grid>
              <Grid item xs="auto">
                <Button variant="outlined" type="submit">
                  Search
                </Button>
              </Grid>
            </Grid>

            <Grid container sx={{ minHeight: 300, my: 2, overflow: "scroll" }}>
              {loading &&
                Array.from({ length: 6 }, (x, i) => (
                  <Grid item xs={12} key={i}>
                    <Skeleton animation="wave" width="100%" height={50} />
                  </Grid>
                ))}
              {!loading &&
                searchResults?.map((searchResult) => (
                  <Grid
                    item
                    xs={12}
                    container
                    alignItems="center"
                    sx={{ borderBottom: `1px solid ${grey[400]}`, py: 2 }}
                    key={searchResult.domain}
                  >
                    <Grid
                      item
                      container
                      columnSpacing={1}
                      alignItems="center"
                      xs
                    >
                      <Grid item xs="auto">
                        {searchResult.domain}
                      </Grid>
                      {searchResult.available === false && (
                        <Grid item xs="auto">
                          <Chip
                            label="Unavailable"
                            size="small"
                            sx={{ fontSize: 12 }}
                            color="warning"
                          />
                        </Grid>
                      )}
                      {searchResult.domain.toLowerCase() ===
                        domain.toLowerCase() && (
                        <Grid item xs="auto">
                          <Chip
                            label="Perfect Match"
                            size="small"
                            sx={{ fontSize: 12 }}
                            color="primary"
                          />
                        </Grid>
                      )}
                    </Grid>
                    {searchResult.available === null && (
                      <Skeleton width="140px" height="30px" />
                    )}
                    {searchResult.available && (
                      <Grid item xs="auto">
                        ${searchResult.price}/year
                      </Grid>
                    )}
                    {searchResult.available && (
                      <Grid item xs="auto">
                        <Button
                          sx={{ mx: 2 }}
                          variant="contained"
                          disableElevation
                          size="small"
                          onClick={() => {
                            trackEvent("Custom Domain Dialog Selected Domain", {
                              domain: searchResult.domain,
                              price: searchResult.price,
                            });

                            setStep(1);
                            setSelectedDomain((prev) => {
                              if (!prev) {
                                const [first_name = "", ...rest] = (
                                  currentUser?.name || ""
                                ).split(" ");
                                const last_name = rest.join(" ");

                                return {
                                  ...searchResult,
                                  first_name,
                                  last_name,
                                  username: "hello",
                                };
                              }

                              return {
                                ...prev,
                                domain: searchResult.domain,
                                available: searchResult.available,
                                price: searchResult.price,
                              };
                            });
                          }}
                        >
                          Select
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                ))}
            </Grid>
          </DialogContent>
        </form>
      )}
      {step === 1 && (
        <>
          {" "}
          <DialogTitle>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography gutterBottom variant="h4">
                Setting up your Email
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container justifyContent="center">
              <Grid container item xs={12} sm={10} md={8} columnGap={1}>
                <Grid item container gap={1} xs={12}>
                  <Grid item xs>
                    <FormHelperText>First Name</FormHelperText>
                    <TextField
                      value={selectedDomain?.first_name || ""}
                      onChange={(e: CustomEvent) => {
                        const { value } = e.target;
                        setSelectedDomain((prev) => {
                          if (!prev) return null;
                          return {
                            ...prev,
                            first_name: value,
                          };
                        });
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs>
                    <FormHelperText>Last Name</FormHelperText>
                    <TextField
                      value={selectedDomain?.last_name || ""}
                      onChange={(e: CustomEvent) => {
                        const { value } = e.target;
                        setSelectedDomain((prev) => {
                          if (!prev) return null;
                          return {
                            ...prev,
                            last_name: value,
                          };
                        });
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <FormHelperText>Email</FormHelperText>
                  <TextField
                    value={selectedDomain?.username || ""}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          @{selectedDomain?.domain}
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e: CustomEvent) => {
                      const { value } = e.target;
                      setSelectedDomain((prev) => {
                        if (!prev) return null;
                        return {
                          ...prev,
                          username: value,
                        };
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
        </>
      )}
      {step === 2 && plans && (
        <Box
          sx={{
            height: 550,
            backgroundColor: "white",
            pb: 2,
            overflow: "hidden",
          }}
        >
          <StripeCheckout
            openCheckoutDialog={{
              plan: AddOnItemName.custom_email,
              pageSource: window.location.pathname,
              source,
              domainInfo: selectedDomain || undefined,
            }}
            onBack={handleBack}
            onSuccess={() => {
              setStep(3);
            }}
          />
        </Box>
      )}
      {step === 3 && (
        <>
          <DialogTitle>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography gutterBottom variant="h4">
                🎉 You're all set!
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography paragraph>
              We're setting up your professional Gmail inbox now with your new
              custom domain: <strong>{selectedDomain?.domain}</strong>. This
              typically takes 1-2 business days. Once it's ready, you'll receive
              an email with login details and everything you need to get
              started.
            </Typography>
            <Typography gutterBottom>Thank you for choosing Bento!</Typography>
          </DialogContent>
        </>
      )}
      <DialogActions style={{ justifyContent: "space-between" }}>
        {step !== 2 && (
          <Button color="secondary" onClick={handleBack}>
            {step === 0 || step === 3 ? "Close" : "Back"}
          </Button>
        )}
        {step === 1 && (
          <Button
            color="primary"
            disableElevation
            variant="contained"
            onClick={() => {
              trackEvent("Custom Domain Dialog Entered Email Username", {
                step,
                username: selectedDomain?.username,
                "First Name": selectedDomain?.first_name,
                "Last Name": selectedDomain?.last_name,
                domain: selectedDomain?.domain,
                price: selectedDomain?.price,
              });
              setStep(2);
            }}
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomDomainDialog;
